import BaseModal from 'components/CommonComponents/BaseModal'
import React from 'react'
import { IPop } from 'constants/interfaces'
import { Box, Flex, Image, VisuallyHidden } from '@chakra-ui/core'
import { useId } from '@reach/auto-id'
import useLocaleText from 'components/useLocaleText'
import colors from 'constants/colors'
import { CloseButton } from 'components/ui/Button'
import PopAvatar from './PopAvatar'
import PopSubmitBtn from './PopSubmitBtn'
import PopBody from './PopBody'
import { LinkPopContent } from './LinkPop'
import { PopType } from './utils'
import { useLinkPopAction } from './hooks/useLinkPopAction'
import { linkPopLiterals } from './model/literals'
import linkPopStyles from './LinkPop.module.css'

interface IProps {
  isOpen: boolean
  onClose: () => void
  firstPopInStack: IPop
  acknowledgeLinkPop?: () => boolean | void | Promise<boolean | void>
  handleSubmit: (popType: number | undefined) => boolean | void | Promise<boolean | void>
}
export const DefaultPop = ({ isOpen, onClose, firstPopInStack, handleSubmit, acknowledgeLinkPop }: IProps) => {
  const ack_btn_text = useLocaleText(firstPopInStack?.ack_btn_text)
  const close = useLocaleText(linkPopLiterals.close)
  const closeHint = useLocaleText(linkPopLiterals.closeHint)
  const closeHintId = `link-pop-close-hint-${useId()}`
  const isLinkPop = firstPopInStack.pop_type === PopType.LINK_POP
  const linkAction = useLinkPopAction(
    isLinkPop ? firstPopInStack.pop_body : undefined,
    () => acknowledgeLinkPop?.(),
    () => {
      handleSubmit(firstPopInStack.pop_type)
    },
    JSON.stringify([firstPopInStack.pk || firstPopInStack.sk, !!firstPopInStack.isPreview])
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={isLinkPop ? () => undefined : onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      overflowY="unset"
      maxWidth={isLinkPop ? 'min(488px, calc(100vw - 64px))' : '32rem'}
      borderRadius={isLinkPop ? '28px' : undefined}
      minWidth={isLinkPop ? '0' : undefined}
      contentClassName={isLinkPop ? linkPopStyles.modal : undefined}
      bodyMaxHeight={isLinkPop ? 'none' : undefined}
      headerPaddingTop={isLinkPop ? '0' : undefined}
      headerPaddingBottom={isLinkPop ? '0' : undefined}
      hideCloseButton={true}
      isShowAnimation={true}
      headerContent={
        <Flex minH={isLinkPop ? '104px' : '100px'}>
          <PopAvatar
            {...firstPopInStack}
            width={isLinkPop ? '160px' : undefined}
            height={isLinkPop ? '160px' : undefined}
            top={isLinkPop ? '-80px' : undefined}
            className={isLinkPop ? linkPopStyles.avatar : undefined}
          />
          {isLinkPop && (
            <>
              <CloseButton
                type="button"
                position="absolute"
                top="-24px"
                left="-24px"
                w="28px"
                minW="28px"
                h="28px"
                p="6px"
                borderRadius="50%"
                bg="white"
                aria-label={close}
                aria-describedby={!linkAction.canClose ? closeHintId : undefined}
                isDisabled={!linkAction.canClose}
                onClick={linkAction.handleClose}
              />
              <VisuallyHidden id={closeHintId}>{closeHint}</VisuallyHidden>
            </>
          )}
          {firstPopInStack?.pop_icon_url && (
            <Box
              width="50px"
              height="50px"
              pos="absolute"
              borderRadius="120px"
              left="30px"
              top="45px"
              background={colors.greyLight3}
            >
              <Image src={firstPopInStack.pop_icon_url} width="40px" height="30px" m="9px auto" />
            </Box>
          )}
        </Flex>
      }
      bodyContent={
        <PopBody
          {...firstPopInStack}
          horizontalPadding={isLinkPop ? '24px' : undefined}
          showCreator={!isLinkPop}
          layout={isLinkPop ? 'link' : 'default'}
          bodyContent={
            isLinkPop ? (
              <LinkPopContent
                key={firstPopInStack.pk || firstPopInStack.sk || firstPopInStack.pop_body}
                isOpen={isOpen}
                pop={firstPopInStack}
                action={linkAction}
              />
            ) : undefined
          }
        />
      }
      footerContent={
        !isLinkPop ? (
          <Flex w="100%" justifyContent="center" alignItems="center" flexDir="column">
            <PopSubmitBtn
              onClose={handleSubmit}
              btnText={ack_btn_text}
              pop_initiator={firstPopInStack.pop_initiator}
              popType={firstPopInStack.pop_type}
            />
          </Flex>
        ) : undefined
      }
    />
  )
}
